import React, { useEffect, useState, Suspense, lazy } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import NewOrderButton from '../../components/NewOrder/NewOrderButton';
import NewOrderModal from '../../components/NewOrder/NewOrderModal';
import { getOrders, getOrderPairs } from '../../services/OrdersService';
import { processError } from '../../services/ServiceUtils';
import OrderRow from './OrderRow';
import Pagination, { getStoredPageSize } from '../../components/Pagination/Pagination';
import SearchSymbol from '../../components/SearchSymbol/SearchSymbol';
import SelectStrategy from '../../components/SelectStrategy/SelectStrategy';
import ViewOrderModal from './ViewOrderModal';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import { getEmptyMessage, getErrorMessage } from '../../utils/MessageUtils';
import { findRowFromActionButton } from '../../utils/DataRowButtonUtils';
import { exportToCsv } from '../../utils/CsvUtils';
import { getCurrentEnvironment, subscribeToCurrentEnvironment } from '../../utils/CurrentEnvironmentStorage';

// Lazy: a lib de gráfico (lightweight-charts, ~68KB) só carrega quando o usuário abre o
// gráfico de uma ordem — fora do bundle principal.
const OrderChartModal = lazy(() => import('./OrderChartModal'));

function Orders() {

    const { symbol } = useParams();

    const [search, setSearch] = useState(symbol || '');
    const [viewOrder, setViewOrder] = useState({});
    const [chartSymbol, setChartSymbol] = useState('');

    const defaultLocation = useLocation();

    function getPage(location) {
        if (!location) location = defaultLocation;
        return new URLSearchParams(location.search).get('page');
    }

    function getSize(location) {
        if (!location) location = defaultLocation;
        return parseInt(new URLSearchParams(location.search).get('size')) || getStoredPageSize();
    }

    const history = useHistory();

    const [orders, setOrders] = useState([]);
    const [pairGroups, setPairGroups] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(parseInt(getPage()) || 1);
    const [size, setSize] = useState(getSize());
    const [environment, setEnvironment] = useState(getCurrentEnvironment());
    const [strategyId, setStrategyId] = useState('');
    const [paired, setPaired] = useState(false);

    const [notification, setNotification] = useState(getEmptyMessage());

    useEffect(() => subscribeToCurrentEnvironment(setEnvironment), []);

    useEffect(() => {
        return history.listen(location => {
            setPage(getPage(location));
            setSize(getSize(location));
        });
    }, [history]); // eslint-disable-line react-hooks/exhaustive-deps

    function getOrdersCall() {
        if (paired) {
            // Visão pareada: o servidor pareia FIFO sobre TODAS as ordens do filtro
            // (cross-page) e devolve os pares já paginados.
            getOrderPairs(search, page || 1, environment, size, strategyId)
                .then(result => {
                    setPairGroups(result.rows || []);
                    setCount(result.count);
                })
                .catch(err => setNotification(getErrorMessage(processError(err))));
        } else {
            getOrders(search, page || 1, environment, size, strategyId)
                .then(result => {
                    setOrders(result.rows);
                    setCount(result.count);
                })
                .catch(err => setNotification(getErrorMessage(processError(err))));
        }
    }

    useEffect(() => {
        getOrdersCall();
    }, [page, search, environment, size, strategyId, paired]); // eslint-disable-line react-hooks/exhaustive-deps

    // SelectStrategy emite 2 eventos (strategyId + automationIds); só reagimos ao strategyId.
    function onStrategyChange(event) {
        if (event.target.id !== 'strategyId') return;
        setStrategyId(event.target.value || '');
    }

    function onCloseViewOrderModal(reloadPage) {
        if (reloadPage)
            history.go(0); // recarrega a tela
    }

    function onOrderSubmit(order, reloadPage) {
        if (reloadPage)
            history.go(0); // recarrega a tela
    }

    function onSearchChange(event) {
        setSearch(event.target.value);
    }

    // Ordens efetivamente exibidas na tela: na visão plana é `orders`; na pareada é a lista
    // achatada (compra+venda) dos pares do servidor. Usada pelo "Ver" e pelo export CSV.
    function getVisibleOrders() {
        if (!paired) return orders;
        return pairGroups.flatMap(g => [g.buy, g.sell].filter(Boolean));
    }

    function onExportClick() {
        const headers = ['ID', 'Símbolo', 'Lado', 'Tipo', 'Quantidade', 'Preço', 'Líquido', 'Status', 'Data'];
        const rows = getVisibleOrders().map(o => [
            o.clientOrderId,
            o.symbol,
            o.side,
            o.type,
            o.quantity,
            o.limitPrice || o.stopPrice || '',
            o.net,
            o.status,
            o.transactTime ? new Date(o.transactTime).toLocaleString('pt-BR') : ''
        ]);
        exportToCsv(`ordens_${search || 'all'}_${Date.now()}.csv`, headers, rows);
    }

    function onViewClick(event) {
        setViewOrder(findRowFromActionButton(event, getVisibleOrders()));
    }

    // Recebe a ordem direto (sem lookup por DOM id) e plota o gráfico do símbolo dela.
    function onChartClick(order) {
        if (order && order.symbol) setChartSymbol(order.symbol);
    }

    // Alterna lista plana ↔ pares. O pareamento agora é GLOBAL (servidor pareia sobre todas
    // as ordens do filtro e pagina por pares), então reseta pra página 1 ao trocar — a
    // contagem de páginas muda (nº de pares ≤ nº de ordens).
    function togglePaired() {
        setPaired(p => !p);
        history.push(`${window.location.pathname}?page=1&size=${size}`);
    }

    return (
        <React.Fragment>
            <Menu />
            <main className='content'>
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className='d-block mb-4 mb-md-0'>
                        <h2 className='h4'>Ordens</h2>
                    </div>
                    <div className='btn-toolbar mb-2 mb-md-0'>
                        <div className='d-inline-flex align-items-center'>
                            <NewOrderButton />
                        </div>
                        <div className='btn-group ms-2 ms-lg-3'>
                            <SelectStrategy strategyId={strategyId} allLabel='Todas as estratégias' onChange={onStrategyChange} />
                        </div>
                        <div className='btn-group ms-2'>
                            <SearchSymbol placeholder="Buscar símbolo" onChange={onSearchChange} />
                        </div>
                        <div className='btn-group ms-2'>
                            <button className={`btn btn-sm ${paired ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={togglePaired} title='Alternar entre lista e pares compra/venda'>
                                {paired ? '☰ Lista' : '⇅ Pares'}
                            </button>
                        </div>
                        <div className='btn-group ms-2'>
                            <button className='btn btn-sm btn-outline-secondary' onClick={onExportClick} disabled={!getVisibleOrders().length} title='Exportar CSV'>
                                CSV
                            </button>
                        </div>
                    </div>
                </div>
                <div className="card card-body border-0 shadow table-wrapper table-responsive">
                    <table className='table table-hover'>
                        <thead>
                            <tr>
                                <th className='border-gray-200'>Ordem</th>
                                <th className='border-gray-200 text-center'>Data</th>
                                <th className='border-gray-200 text-center'>Qtd</th>
                                <th className='border-gray-200 text-center'>Líquido</th>
                                <th className='border-gray-200 text-center'>Lucro</th>
                                <th className='border-gray-200 text-center'>Status</th>
                                <th className='border-gray-200 text-center'>Estratégia</th>
                                <th className='border-gray-200 text-center'>Ver</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                paired
                                    ? (pairGroups && pairGroups.length
                                        ? pairGroups.map((g, i) => {
                                            // Zebra POR GRUPO (não por linha): grupos alternados ganham um fundo
                                            // sutil, pra separar visualmente onde um par termina e o próximo começa.
                                            // A faixa colorida (accent) continua codificando o ESTADO do par.
                                            const zebra = i % 2 === 1;
                                            if (g.type === 'pair') return (
                                                <React.Fragment key={`pair-${g.sell.clientOrderId}`}>
                                                    <OrderRow data={g.buy} onClick={onViewClick} onChartClick={onChartClick} accent='#28a745' zebra={zebra} />
                                                    <OrderRow data={g.sell} onClick={onViewClick} onChartClick={onChartClick} accent='#28a745' zebra={zebra} />
                                                </React.Fragment>
                                            );
                                            if (g.type === 'open') {
                                                // A perna que ABRE depende do mercado: spot abre COMPRANDO (g.buy);
                                                // short de futuros abre VENDENDO (g.sell, g.buy null). Renderiza a que existir.
                                                const o = g.buy || g.sell;
                                                return (
                                                    <OrderRow key={o.clientOrderId} data={o} onClick={onViewClick} onChartClick={onChartClick} accent='#ffc107' openPosition={true} zebra={zebra} />
                                                );
                                            }
                                            // órfã: venda sem compra casada (spot) OU compra avulsa (fecho de futuros sem abertura na fila).
                                            const o = g.sell || g.buy;
                                            return (
                                                <OrderRow key={o.clientOrderId} data={o} onClick={onViewClick} onChartClick={onChartClick} accent='#adb5bd' zebra={zebra} />
                                            );
                                        })
                                        : <React.Fragment></React.Fragment>)
                                    : (orders && orders.length
                                        ? orders.map(order => (
                                            <OrderRow key={order.clientOrderId} data={order} onClick={onViewClick} onChartClick={onChartClick} />
                                        ))
                                        : <React.Fragment></React.Fragment>)
                            }
                        </tbody>
                    </table>
                    <Pagination count={count} size={size} />
                </div>
                <Footer />
            </main>
            <ViewOrderModal data={viewOrder} onClose={onCloseViewOrderModal} />
            {chartSymbol &&
                <Suspense fallback={null}>
                    <OrderChartModal symbol={chartSymbol} environment={environment} strategyId={strategyId} onClose={() => setChartSymbol('')} />
                </Suspense>}
            <NewOrderModal onSubmit={onOrderSubmit} />
            <Toast type={notification.type} text={notification.text} />
        </React.Fragment>
    )
}

export default Orders;